import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { GanttChart, ArrowLeft, Lock, Loader2, CheckCircle, AlertCircle } from 'lucide-react';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setLoading(true);
    
    try {
      await authAPI.resetPassword(token, password);
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="font-outfit text-3xl font-bold text-zinc-900 mb-2">
            Invalid Reset Link
          </h1>
          <p className="text-zinc-600 mb-8">
            This password reset link is invalid or has expired.
          </p>
          <Link to="/forgot-password">
            <Button className="w-full h-12 bg-primary hover:bg-orange-600 text-white rounded-xl">
              Request New Link
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-zinc-900 p-12 flex-col justify-between">
        <div>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
              <GanttChart className="w-6 h-6 text-white" />
            </div>
            <span className="font-outfit font-bold text-2xl text-white">ProjectOS</span>
          </Link>
        </div>
        <div className="max-w-md">
          <h2 className="font-outfit text-4xl font-bold text-white mb-4">
            Set Your New Password
          </h2>
          <p className="text-zinc-400 text-lg">
            Choose a strong password that you haven't used before.
          </p>
        </div>
        <div className="text-zinc-500 text-sm">
          Remember your password?{' '}
          <Link to="/login" className="text-white hover:underline">Sign in</Link>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex flex-col">
        <div className="p-6 flex justify-between items-center">
          <Link to="/login" className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to login
          </Link>
          <div className="lg:hidden flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <GanttChart className="w-5 h-5 text-white" />
            </div>
            <span className="font-outfit font-bold text-xl">ProjectOS</span>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md">
            {success ? (
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h1 className="font-outfit text-3xl font-bold text-zinc-900 mb-2">
                  Password Reset!
                </h1>
                <p className="text-zinc-600 mb-8">
                  Your password has been successfully reset.<br />
                  You can now sign in with your new password.
                </p>
                <Button 
                  onClick={() => navigate('/login')}
                  className="w-full h-12 bg-primary hover:bg-orange-600 text-white rounded-xl"
                >
                  Sign In
                </Button>
              </div>
            ) : (
              <>
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Lock className="w-8 h-8 text-orange-600" />
                </div>
                <h1 className="font-outfit text-3xl font-bold text-zinc-900 mb-2 text-center">
                  Create New Password
                </h1>
                <p className="text-zinc-600 mb-8 text-center">
                  Enter your new password below.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-zinc-700">New Password</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      data-testid="reset-password-input"
                      className="h-12 rounded-xl border-zinc-200 focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                    />
                    <p className="text-xs text-zinc-500">Must be at least 6 characters</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className="text-zinc-700">Confirm Password</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      data-testid="reset-confirm-password-input"
                      className="h-12 rounded-xl border-zinc-200 focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="reset-submit-btn"
                    className="w-full h-12 bg-primary hover:bg-orange-600 text-white rounded-xl font-medium transition-all active:scale-[0.98]"
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Reset Password'}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
